import { useState, useEffect, useCallback } from "react";
import { AdminCard, AdminCardHead, LoadFailedBanner } from "components/admin/ui";
import { adminGet, adminPut } from "lib/adminClient";

// Brand palette for the avatar accent colour.
const COLORS = [
  ["#005C40", "Green"],
  ["#B8002B", "Red"],
  ["#9B7200", "Gold"],
  ["#8B1A4A", "Rose"],
  ["#003d2b", "Dark Green"],
  ["#2d6ab0", "Blue"],
];

const initialsFrom = (name) =>
  name
    .replace(/\b(Prof|Dr|Mr|Mrs|Ms|Md)\.?/gi, "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "—";

const blank = () => ({ role: "", name: "", extra: "", initials: "", color: "#005C40" });

const inputCls =
  "w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green";

export default function ManagementPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);

  const [loadFailed, setLoadFailed] = useState(false);


  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setError("");
    try {
      const data = await adminGet("/admin/content/management");
      setMembers(Array.isArray(data) ? data : []);
    } catch (e) {
      setLoadFailed(true);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = (i, field, value) =>
    setMembers((m) => m.map((x, idx) => (idx === i ? { ...x, [field]: value } : x)));
  const remove = (i) => setMembers((m) => m.filter((_, idx) => idx !== i));
  const add = () => setMembers((m) => [...m, blank()]);
  const move = (i, dir) =>
    setMembers((m) => {
      const j = i + dir;
      if (j < 0 || j >= m.length) return m;
      const copy = [...m];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });

  const save = async () => {
    if (loadFailed) {
      setError("Content didn't load, so saving now would erase it. Reload the page first.");
      return;
    }
    setSaving(true);
    setStatus("");
    setError("");
    try {
      // Auto-fill initials from the name where left blank.
      const cleaned = members
        .filter((x) => x.name.trim() || x.role.trim())
        .map((x) => ({ ...x, initials: x.initials.trim() || initialsFrom(x.name) }));
      const saved = await adminPut("/admin/content/management", cleaned);
      setMembers(Array.isArray(saved) ? saved : cleaned);
      setStatus("Saved.");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-1 py-8 text-muted text-sm">Loading…</div>;

  return (
    <div className="animate-fadeIn">
      {loadFailed && <LoadFailedBanner onRetry={load} />}

      {error && (
        <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>
      )}

      <AdminCard>
        <AdminCardHead
          title={`Management & Governing Body (${members.length})`}
          action={
            <button
              onClick={add}
              className="bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90"
            >
              + Add Member
            </button>
          }
        />

        <div className="p-[22px] flex flex-col gap-4">
          {members.length === 0 && (
            <div className="text-center text-muted text-sm py-6">
              No members yet — click “Add Member”.
            </div>
          )}

          {members.map((m, i) => (
            <div key={i} className="border border-line rounded-xl p-4 flex gap-4 items-start">
              {/* Avatar preview */}
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center font-serif font-bold text-lg shrink-0"
                style={{ background: (m.color || "#005C40") + "18", color: m.color || "#005C40", border: `2px solid ${(m.color || "#005C40")}50` }}
              >
                {(m.initials || initialsFrom(m.name || "")).slice(0, 2)}
              </div>

              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  value={m.name}
                  onChange={(e) => update(i, "name", e.target.value)}
                  placeholder="Full name"
                  className={inputCls}
                />
                <input
                  value={m.role}
                  onChange={(e) => update(i, "role", e.target.value)}
                  placeholder="Role / designation"
                  className={inputCls}
                />
                <input
                  value={m.extra}
                  onChange={(e) => update(i, "extra", e.target.value)}
                  placeholder="Qualification / note"
                  className={`${inputCls} md:col-span-2`}
                />
                <input
                  value={m.initials}
                  onChange={(e) => update(i, "initials", e.target.value)}
                  placeholder="Initials (auto if blank)"
                  maxLength={3}
                  className={inputCls}
                />
                <select
                  value={m.color}
                  onChange={(e) => update(i, "color", e.target.value)}
                  className={`${inputCls} bg-white`}
                >
                  {COLORS.map(([hex, name]) => (
                    <option key={hex} value={hex}>
                      {name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1.5 shrink-0">
                <button
                  onClick={() => move(i, -1)}
                  title="Move up"
                  className="bg-[#f2f4f6] border-none rounded-md px-2 py-1 cursor-pointer text-xs"
                >
                  ↑
                </button>
                <button
                  onClick={() => move(i, 1)}
                  title="Move down"
                  className="bg-[#f2f4f6] border-none rounded-md px-2 py-1 cursor-pointer text-xs"
                >
                  ↓
                </button>
                <button
                  onClick={() => remove(i)}
                  title="Remove"
                  className="bg-[#fdeaea] border-none rounded-md px-2 py-1 cursor-pointer text-xs text-[#c44]"
                >
                  🗑
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="px-[22px] py-[18px] border-t border-[#eef1f3] flex items-center gap-3">
          <button
            onClick={save}
            disabled={saving}
            className="bg-green text-white border-none rounded-[9px] px-[26px] py-[11px] font-sans font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
          {status && <span className="text-[13px] text-[#1a9f5e] font-semibold">{status}</span>}
        </div>
      </AdminCard>
    </div>
  );
}
